import { Check, ShieldAlert, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { GlassPanel, Pill, Surface } from "@/components/glass";

type Status =
	| { kind: "input" }
	| { kind: "checking" }
	| { kind: "ready"; userCode: string }
	| { kind: "approving" }
	| { kind: "approved" }
	| { kind: "denying" }
	| { kind: "denied" }
	| { kind: "error"; message: string };

export default function Device() {
	const [params] = useSearchParams();
	const initialCode = params.get("user_code") ?? "";
	const [code, setCode] = useState(initialCode);
	const [status, setStatus] = useState<Status>(
		initialCode ? { kind: "checking" } : { kind: "input" },
	);

	useEffect(() => {
		if (initialCode) verifyCode(initialCode).then(setStatus);
	}, [initialCode]);

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		const trimmed = code.trim();
		if (!trimmed) return;
		setStatus({ kind: "checking" });
		setStatus(await verifyCode(trimmed));
	}

	async function onApprove() {
		if (status.kind !== "ready") return;
		setStatus({ kind: "approving" });
		try {
			const r = await fetch("/api/auth/device/approve", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userCode: status.userCode }),
			});
			if (!r.ok) {
				const body = await r.json().catch(() => ({}));
				setStatus({
					kind: "error",
					message: body.error_description || `approval failed (${r.status})`,
				});
				return;
			}
			setStatus({ kind: "approved" });
		} catch (e) {
			setStatus({
				kind: "error",
				message: e instanceof Error ? e.message : "approval failed",
			});
		}
	}

	async function onDeny() {
		if (status.kind !== "ready") return;
		setStatus({ kind: "denying" });
		try {
			await fetch("/api/auth/device/deny", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userCode: status.userCode }),
			});
			setStatus({ kind: "denied" });
		} catch (e) {
			setStatus({
				kind: "error",
				message: e instanceof Error ? e.message : "deny failed",
			});
		}
	}

	return (
		<Surface className="min-h-screen flex items-center justify-center p-4">
			<GlassPanel className="p-7 w-full max-w-md space-y-5">
				<div className="text-center space-y-2">
					<h1 className="text-[15px] font-medium tracking-tight">Authorize device</h1>
					<p className="text-[12px] text-[var(--color-text-muted)]">
						Confirm the code shown by your CLI to grant it access to your account.
					</p>
				</div>

				{status.kind === "input" && (
					<form onSubmit={onSubmit} className="space-y-3">
						<input
							type="text"
							value={code}
							onChange={(e) => setCode(e.target.value.toUpperCase())}
							placeholder="ABCD-1234"
							autoFocus
							autoComplete="off"
							spellCheck={false}
							className="w-full h-10 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-center mono tracking-widest text-[14px] outline-none focus:border-[var(--color-border-hi)]"
						/>
						<div className="flex justify-center">
							<Pill type="submit" disabled={!code.trim()}>Continue</Pill>
						</div>
					</form>
				)}

				{status.kind === "checking" && (
					<p className="text-center text-[12px] text-[var(--color-text-muted)]">Checking code…</p>
				)}

				{status.kind === "ready" && (
					<div className="space-y-4">
						<div className="glass rounded-[12px] p-3 space-y-1">
							<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">Code</div>
							<div className="mono text-[14px] tracking-widest">{status.userCode}</div>
						</div>
						<p className="text-[12px] text-[var(--color-text-muted)]">
							Approving will sign the device into your account. Only continue if this code matches what your CLI just printed.
						</p>
						<div className="flex gap-2 justify-center">
							<Pill onClick={onDeny}>
								<X className="w-3.5 h-3.5" /> Deny
							</Pill>
							<Pill onClick={onApprove}>
								<Check className="w-3.5 h-3.5" /> Approve
							</Pill>
						</div>
					</div>
				)}

				{(status.kind === "approving" || status.kind === "denying") && (
					<p className="text-center text-[12px] text-[var(--color-text-muted)]">
						{status.kind === "approving" ? "Approving…" : "Denying…"}
					</p>
				)}

				{status.kind === "approved" && (
					<div className="glass rounded-[12px] p-5 text-center space-y-2">
						<div className="flex justify-center">
							<Check className="w-7 h-7 text-[var(--color-success)]" />
						</div>
						<h2 className="text-[13px] font-medium">Device approved</h2>
						<p className="text-[12px] text-[var(--color-text-muted)]">
							You can return to your CLI. It will pick up the access token shortly.
						</p>
					</div>
				)}

				{status.kind === "denied" && (
					<div className="glass rounded-[12px] p-5 text-center space-y-2">
						<div className="flex justify-center">
							<X className="w-7 h-7 text-[var(--color-text-dim)]" />
						</div>
						<h2 className="text-[13px] font-medium">Device denied</h2>
						<p className="text-[12px] text-[var(--color-text-muted)]">
							The CLI will report the denial.
						</p>
					</div>
				)}

				{status.kind === "error" && (
					<div className="rounded-[12px] border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.06)] p-3 space-y-2">
						<div className="flex items-center gap-2 text-[12px] font-medium text-[var(--color-danger)]">
							<ShieldAlert className="w-3.5 h-3.5" /> Something went wrong
						</div>
						<p className="text-[12px] text-[var(--color-text-muted)]">{status.message}</p>
						<button
							type="button"
							onClick={() => setStatus({ kind: "input" })}
							className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] underline underline-offset-2"
						>
							Try again
						</button>
					</div>
				)}
			</GlassPanel>
		</Surface>
	);
}

async function verifyCode(userCode: string): Promise<Status> {
	try {
		const r = await fetch(
			`/api/auth/device?user_code=${encodeURIComponent(userCode)}`,
			{ credentials: "include" },
		);
		if (!r.ok) {
			const body = await r.json().catch(() => ({}));
			return {
				kind: "error",
				message: body.error_description || `code rejected (${r.status})`,
			};
		}
		const body = (await r.json()) as { status?: string };
		if (body.status !== "pending") {
			return {
				kind: "error",
				message: `this code is already ${body.status ?? "processed"}`,
			};
		}
		return { kind: "ready", userCode };
	} catch (e) {
		return {
			kind: "error",
			message: e instanceof Error ? e.message : "unknown error",
		};
	}
}
